// Functional device-category taxonomy for the owner inventory. Devices group
// into a small set of human buckets so the list is
// scannable instead of a flat list of intermingled brands and functions.
//
// Classification is DETERMINISTIC, leading with the standardized NMEA 2000
// device class (`deviceClass.name`), refined by device function / display
// name keywords, then manufacturer as a final hint. Defined by a marine-
// electronics taxonomy pass; mirrors the prototype's deriveCategory so the
// same device always lands in the same bucket regardless of label edits.

import type { VesselDevice } from './useBoatDevices';

export type CategoryKey = 'navigation' | 'instruments' | 'energy' | 'engine' | 'safety' | 'comfort';

export interface DeviceCategory {
  key: CategoryKey;
  label: string;
  /** Fixed canonical inventory order (operational priority, not alphabetical). */
  order: number;
}

// Comfort & Onboard is last and doubles as the catch-all so no device is ever
// orphaned — pure network plumbing and anything unclassifiable land here.
export const CATEGORIES: readonly DeviceCategory[] = [
  { key: 'navigation', label: 'Navigation', order: 1 },
  { key: 'instruments', label: 'Instruments & Environment', order: 2 },
  { key: 'energy', label: 'Power & Energy', order: 3 },
  { key: 'engine', label: 'Engine & Propulsion', order: 4 },
  { key: 'safety', label: 'Safety', order: 5 },
  { key: 'comfort', label: 'Comfort & Onboard', order: 6 },
];

export const CATEGORY_BY_KEY: Record<CategoryKey, DeviceCategory> = Object.fromEntries(
  CATEGORIES.map((c) => [c.key, c])
) as Record<CategoryKey, DeviceCategory>;

/**
 * Deterministic waterfall, first match wins. Reads the immutable identity
 * signals (deviceClass / deviceFunction / displayName / manufacturer) so the
 * bucket is stable across firmware and label edits.
 */
export function deviceCategory(d: VesselDevice): CategoryKey {
  const cls = (d.deviceClass?.name ?? '').toLowerCase();
  const fn = (d.deviceFunction?.name ?? '').toLowerCase();
  const name = (d.displayName ?? '').toLowerCase();
  const mfr = (d.manufacturer?.name ?? '').toLowerCase();
  const hay = `${fn} ${name}`; // refinement haystack
  const isChartplotter = /chartplotter|chart plotter|\bmfd\b|nav station/.test(hay);

  // STEP 1 — lead with the standardized NMEA 2000 device class. "Display" is a
  // form factor, not a function, so never bucket it by class alone — refine it.
  if (cls !== 'display') {
    if (/navigation|steering|control/.test(cls)) return 'navigation';
    if (/propulsion|engine/.test(cls)) return 'engine';
    if (/electrical generation|electrical distribution|power/.test(cls)) return 'energy';
    if (/internal environment|hvac|entertainment/.test(cls)) return 'comfort';
    if (/safety/.test(cls)) return 'safety';
    if (/inter.?intranetwork|sensor (communication )?interface/.test(cls)) return 'comfort';
  }

  // STEP 2 — refine ambiguous / Display / generic / unknown classes by function
  // then name keywords.
  if (
    /chartplotter|chart plotter|\bmfd\b|gnss|\bgps\b|gps antenna|autopilot|pilot|heading|compass|radar|nav station/.test(
      hay
    )
  )
    return 'navigation';
  if (
    !isChartplotter &&
    /wind|depth|speed|\blog\b|\bdst\b|tridata|multi[- ]?function display|data display|temperature|barometer|pressure/.test(
      hay
    )
  )
    return 'instruments';
  if (
    /battery|\bbmv\b|shunt|inverter|charger|solar|\bmppt\b|alternator|dc[- ]?dc|shore power|switch.*panel|distribution|tank.*level/.test(
      hay
    )
  )
    return 'energy';
  if (
    /engine|\becu\b|\brpm\b|tachometer|transmission|fuel (rate|flow)|thruster|rudder|trim|exhaust temp|oil pressure|coolant/.test(
      hay
    )
  )
    return 'engine';
  if (
    /\bais\b|transponder|\bdsc\b|\bvhf\b|epirb|\bplb\b|\bmob\b|man overboard|bilge|high water|float switch|fire|smoke|co detector|gas detector|alarm panel/.test(
      hay
    )
  )
    return 'safety';
  if (
    /stereo|audio|amplifier|speaker|media|\btv\b|hvac|air ?con|heater|climate|refrigerat|fridge|lighting|dimmer/.test(
      hay
    )
  )
    return 'comfort';
  if (/gateway|bridge|router|switch|wifi|ethernet/.test(hay)) return 'comfort'; // pure plumbing

  // STEP 3 — manufacturer tiebreaker (a hint, never an override of an explicit class).
  if (/victron|mastervolt|balmar|blue sea/.test(mfr)) return 'energy';
  if (/fusion|jl audio/.test(mfr)) return 'comfort';
  if (/vesper|\bacr\b/.test(mfr)) return 'safety';

  // STEP 4 — fallback bucket.
  return 'comfort';
}

export interface CategorySection {
  category: DeviceCategory;
  devices: VesselDevice[];
}

/**
 * Group devices into the fixed-order functional sections, omitting any empty
 * bucket. Within a section, attention-worthy (cold/never) devices float first,
 * then by display name.
 */
export function groupByCategory(devices: VesselDevice[]): CategorySection[] {
  return CATEGORIES.map((category) => ({
    category,
    devices: devices.filter((d) => deviceCategory(d) === category.key),
  })).filter((s) => s.devices.length > 0);
}
